import {testStakeholderIsStaff, totalWeeksRequired, weeksRequiredTillAction} from "./tools";
import {fetchLocations} from "../factories/locations";

export class AbstractState {
  constructor (history, location, lazy = 0) {
    this.history = history;
    this.location = location;
    this.lazy = lazy;
  }
  getWeek() {
    return weeksRequiredTillAction(this.history[this.history.length -1], this.history);
  }
  getTitle () {
    return "Week " + this.getWeek();
  }
  getStakeholderState (data) {
    return {
      ...data,
      title: this.getTitle(),
      week: this.getWeek(),
    }
  }
  getGenericQITNotes () {
    if (this.lazy) return 'Generic QIT Notes!';
    const history = this.history;
    const prevHistory = history.slice(0, history.length -1);
    const locations = fetchLocations(history, prevHistory);
    const location = locations.find(each => each.id === this.location.id);
    const staff = location.stakeholders.filter(testStakeholderIsStaff);
    const community = location.stakeholders.find(each => Object.keys(each).includes('participation'));
    const motivationText = staff.every(each => each.motivation >= 60)
      ? 'Health facility staff are interested in how the meetings can help them problem solve.'
      : 'Health facility staff seems mostly disinterested in the process.';
    const abilityText = staff.every(each => each.ability >= 60)
      ? 'Health facility staff feels confident using the problem solving tools.'
      : 'Health facility staff feels unsure how to use the problem solving tools.';
    const participationText = community.participation >= 30
      ? 'Community leaders are attending and participating in the meetings.'
      : 'Community leaders are not attending the meetings.';
    return `
    <p class="font-bold">
      The quality improvement team met at this facility:
    </p>
    <ul class="my-3 list-disc list-inside">
      <li>
        ${motivationText}
      </li>
      <li>
        ${abilityText}
      </li>
      <li>
        ${participationText}
      </li>
    </ul>`
  }
  getMicroPlanningNotes () {
    if (this.lazy) return 'Generic Micro Planning Notes!';
    const history = this.history;
    const prevHistory = history.slice(0, history.length -1);
    const locations = fetchLocations(history, prevHistory);
    const location = locations.find(each => each.id === this.location.id);
    const healthFacilityText = location.adoption < 10
      ? "The health facility staff is still unsure how to use local data for micro planning."
      : location.adoption < 80
        ? "The health facility staff is able to use some local data when creating the micro plans."
        : "Health facility staff is able to actively analyze their local data and use it when creating the micro plans.";
    const community = location.stakeholders.find(each => Object.keys(each).includes('participation'));
    const communityText = community.participation < 30
      ? "Community leaders do not attend the meeting."
      : "Community leaders attend and provide active input into the planning process.";
    return `
        <p class="font-bold">
          The quality improvement team had a special meeting to engage in micro planning for the next year:
        </p>
        <ul class="my-3 list-disc list-inside">
          <li>
            ${healthFacilityText}
          </li>
          <li>
            ${communityText}
          </li>
        </ul>`;
  }
}

export class AbstractQITMeetingState extends AbstractState {
  getWeek() {
    return totalWeeksRequired(this.history ,1);
  }
  getTitle() {
    return "Week " + this.getWeek() + ": QIT Meeting";
  }
}

export class AfterInterviewsState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Interviews";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "EPI") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “This is an interesting initiative. I just worry that I will not have time to support it.”
        </p>
        <p class="my-3">
          This district’s EPI focal point thinks that this change could generate interesting solutions to specific local problems. However, he is worried about the overall workload that will be required to supervise and support facility managers during implementation of this change.
        </p>`
      });
    }
    if (stakeholder.id === "HO") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “Asking facilities to take on more decision making sounds very inefficient to me. I think things are fine the way they are.”
        </p>
        <p class="my-3">
          The district health officer has concerns about giving decision-making authority to facility managers.<br/><br/>
          He wants to closely follow the progress of the change at these three facilities before committing to implementing the change at other facilities in this district.
        </p>`
      });
    }
  }
}

export class FromDataAnalysisState extends AbstractState {
  getStakeholderState (stakeholder) {}
}

export class GoAndSeeState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": “Go and See”";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "HO") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “Visiting another district where this was being implemented was very interesting. It seems like they have had really good results. I still want to see how it goes for us, but I am more optimistic now.”
        </p>`
      })
    }
  }
}

export class QITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {}
}

export class FirstSuccessfulQITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {}
}

export class FollowupQITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {}
}

export class MicroPlanningMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {}
}

export class WhenReaches80PercentAdoptionState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Adoption";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "EPI") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “This initiative is going to help us reach all children in this district. I am committed to providing the supportive supervision required to assist.”
        </p>`
      })
    }
  }
}
